/* eslint-disable */
import _ from "lodash";
import { BookingErrorType, FilterType, BillableDisplayType, PaymentSources } from "utilities/enum-utils";
import Utils from "utilities/Utils";
import { BookingFilterAny, FilterTypeAny } from "../types/booking-filter";
import { resolveDatePresetInFilter } from "./format-filters";

export const formatBookingListingFilters = (payload: {
  filters: BookingFilterAny[];
  id?: string;
  [key: string]: unknown;
}) => {
  if (!payload || !payload.filters) {
    return payload;
  }

  const { filters: _filters, ...payloadWithoutFilters } = payload;

  const resolvedFilters = _filters.map((filter) =>
    resolveDatePresetInFilter(filter as Record<string, unknown>) as BookingFilterAny,
  );
  const nonEmptyFilters = resolvedFilters.filter((filter) => !Utils.isEmpty(filter.values));
  const filters = nonEmptyFilters.reduce((acc, filter) => {
    acc[filter.filter] = filter.values;
    return acc;
  }, {} as Record<FilterTypeAny, BookingFilterAny["values"]>);

  const body = { ...payloadWithoutFilters, ...filters };

  if (body[FilterType.CUSTOMER]) {
    body[FilterType.CUSTOMER] = _.map(body[FilterType.CUSTOMER], (item) => item.value);
  }
  if (body[FilterType.WORKER]) {
    body[FilterType.WORKER] = _.map(body[FilterType.WORKER], (item) => item.value);
  }
  if (body[FilterType.SERVICE_DATE_TIMES]) {
    body[FilterType.SERVICE_DATE_TIMES] = _.map(body[FilterType.SERVICE_DATE_TIMES], (item) => item.value);
  }
  if (body[FilterType.GROUP_SERVICE_SCHEDULES]) {
    body[FilterType.GROUP_SERVICE_SCHEDULES] = _.map(body[FilterType.GROUP_SERVICE_SCHEDULES], (item) => item.value);
  }
  if (body[FilterType.LOCATION_BY_SUBURBS]) {
    body[FilterType.LOCATION_BY_SUBURBS] = _.map(body[FilterType.LOCATION_BY_SUBURBS], (item) => item.value);
  }

  if (body[FilterType.BOOKING_ERROR_TYPES]) {
    let errorFilter = [];
    body[FilterType.BOOKING_ERROR_TYPES].forEach((bookingErrorType) => {
      if (_.isArray(bookingErrorType)) {
        errorFilter = [...errorFilter, ...bookingErrorType];
      } else {
        errorFilter.push(bookingErrorType);
      }
    });
    if (
      _.find(errorFilter, (error) => error === BookingErrorType.BookingRecordedTimeWarning) &&
      !_.find(errorFilter, (error) => error === BookingErrorType.BookingActualTimeWarning)
    ) {
      errorFilter.push(BookingErrorType.BookingActualTimeWarning);
    } else if (_.find(errorFilter, (error) => error === BookingErrorType.BookingActualTimeWarning)) {
      errorFilter = _.filter(errorFilter, (error) => error !== BookingErrorType.BookingActualTimeWarning);
    }
    body[FilterType.BOOKING_ERROR_TYPES] = errorFilter;
  }

  if (body[FilterType.RECURRING]) {
    const isRecurringTrue = !!_.find(body[FilterType.RECURRING], (filter) => filter === "YES");
    const isRecurringFalse = !!_.find(body[FilterType.RECURRING], (filter) => filter === "NO");
    // If both yes and no are selected, remove the filter to get the full list
    if (isRecurringTrue && isRecurringFalse) {
      delete body[FilterType.RECURRING];
    } else {
      body[FilterType.RECURRING] = isRecurringTrue;
    }
  }

  if (body[FilterType.PINNED_ALERTS]) {
    const isPinnedAlertTrue = !!_.find(body[FilterType.PINNED_ALERTS], (filter) => filter === "YES");
    const isPinnedAlertFalse = !!_.find(body[FilterType.PINNED_ALERTS], (filter) => filter === "NO");
    // If both yes and no are selected, remove the filter to get the full list
    if (isPinnedAlertTrue && isPinnedAlertFalse) {
      delete body[FilterType.PINNED_ALERTS];
    } else {
      body[FilterType.PINNED_ALERTS] = isPinnedAlertTrue;
    }
  }

  if (body[FilterType.SLEEPOVER]) {
    const isSleepoverTrue = _.some(body[FilterType.SLEEPOVER], (filter) => filter === "YES");
    const isSleepoverFalse = _.some(body[FilterType.SLEEPOVER], (filter) => filter === "NO");
    // If both yes and no are selected, remove the filter to get the full list
    if (isSleepoverTrue && isSleepoverFalse) {
      delete body[FilterType.SLEEPOVER];
    } else {
      body[FilterType.SLEEPOVER] = isSleepoverTrue;
    }
  }

  if (body[FilterType.BOOKING_BILLING]) {
    body[FilterType.BOOKING_BILLING] = body[FilterType.BOOKING_BILLING] === BillableDisplayType.NON_BILLABLE;
  }

  if (body[FilterType.FUNDING_SOURCES]) {
    body.paymentSourceTypes = _.map(body[FilterType.FUNDING_SOURCES], (item) => {
      // Handle both string values and direct enum values
      const value = typeof item === "string" ? item : item.value;
      return value === PaymentSources.NDIS || value === PaymentSources.VCP ? value : null;
    }).filter(Boolean);
    delete body[FilterType.FUNDING_SOURCES];
  }

  if (body.approvalReadiness != null && Array.isArray(body.approvalReadiness)) {
    const [readiness] = body.approvalReadiness as string[];
    if (readiness === "READY" || readiness === "NEEDS_ACTION") {
      body.approvalReadiness = readiness;
    }
  }

  /** Pinned default views must always send readiness so stale/persisted filterValue cannot show all REQUIRES_APPROVAL rows on "Requires approval". */
  const viewId = (body.id as string | undefined) ?? (body.customViewId as string | undefined);
  const paymentStatuses = body.paymentStatus as string[] | undefined;
  const hasRequiresApproval =
    Array.isArray(paymentStatuses) && paymentStatuses.includes("REQUIRES_APPROVAL");
  if (hasRequiresApproval && (body.approvalReadiness === undefined || body.approvalReadiness === null)) {
    if (viewId === "REQUIRED_APPROVAL") {
      body.approvalReadiness = "READY";
    } else if (viewId === "REQUIRES_ACTION") {
      body.approvalReadiness = "NEEDS_ACTION";
    }
  }

  return body;
};
